"use client";

import { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import { encodeString } from "@/lib/utils";
import Link from "next/link";
import { setCookie, deleteCookie } from "cookies-next";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { TextField, Button, Alert } from "@mui/material";
import authenticationApi from "@/lib/authenticationApi";
import AuthLayout from "@/components/AuthLayout";
import menuItems from "@/lib/menuItems";
import {
  enhancedPrefetchPages,
  extractHrefsFromMenuItems,
  isDevelopmentMode,
} from "@/utils/prefetchUtils";
import WarningModal from "@/components/WarningModal";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [refererUrl, setRefererUrl] = useState("");
  const [isAccountLockedModalOpen, setIsAccountLockedModalOpen] =
    useState(false);
  const [accountLockedMessage, setAccountLockedMessage] = useState(
    "Your account is temporarily locked because of repeated incorrect login attempts. Select FORGOT PASSWORD to reset your password.",
  );
  const router = useRouter();

  const handleAccountLockedConfirm = () => {
    setIsAccountLockedModalOpen(false);
    router.push("/forgot-password");
  };

  // Memoize the menu items to prevent unnecessary re-renders
  const memoizedMenuItems = useMemo(() => menuItems, []);

  useEffect(() => {
    // Clear any existing logout events when login page is opened
    // This prevents old logout events from interfering with new logins
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith("ca-logout-")) {
        localStorage.removeItem(key);
      }
    });
    setRefererUrl('/dashboard');
    localStorage.removeItem('referer_url');
  }, []);

  // Prefetch all menu and submenu pages
  useEffect(() => {
    if (!memoizedMenuItems || memoizedMenuItems.length === 0) return;

    const hrefs = extractHrefsFromMenuItems(memoizedMenuItems);

    if (hrefs.length === 0) {
      console.log("🚀 No valid routes to prefetch");
      return;
    }

    document.title = "CareAscendium - Login";

    // Prefetch pages using the enhanced utility function
    const performPrefetch = async () => {
      const result = await enhancedPrefetchPages({
        router,
        hrefs,
        delay: 100,
        enableLogging: true,
      });

      // Log additional info in development
      if (isDevelopmentMode()) {
        console.log(
          "🔧 Development mode: Using enhanced prefetching with fallbacks",
        );
        console.log(
          "💡 To test full prefetching, run: npm run build && npm run start",
        );
      }
    };

    performPrefetch();
  }, [memoizedMenuItems, router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); // Reset error

    const v_email = email.toLowerCase().trim();
    const trimmedPassword = password.trim();

    const encrypted_password = encodeString(v_email + trimmedPassword);

    setIsSubmitting(true);

    const postParams = {
      email: v_email,
      password: encrypted_password,
    };

    const response = await authenticationApi
      .post(`/v1/user_authentication/authenticate`, postParams)
      .then(async (response: any) => {
        setCookie("token", response.data["token"], { path: "/" });
        setCookie("ca_user_id", response.data["ca_user_id"], { path: "/" });
        setCookie("company_id", response.data["company_id"], { path: "/" });
        setCookie("company_name", response.data["company_name"], { path: "/" });
        setCookie("company_user_id", response.data["company_user_id"], {
          path: "/",
        });
        setCookie("user_type", response.data["user_type"], { path: "/" });
        setCookie("user_type_id", response.data["user_type_id"], { path: "/" });
        setCookie("user_pay_type", response.data["user_pay_type"], {
          path: "/",
        });
        setCookie("user_role", response.data["user_role"], { path: "/" });
        setCookie("user_role_id", response.data["user_role_id"], { path: "/" });
        setCookie("user_full_name", response.data["user_full_name"], {
          path: "/",
        });
        setCookie("first_name", response.data["first_name"], { path: "/" });
        setCookie("middle_name", response.data["middle_name"], { path: "/" });
        setCookie("last_name", response.data["last_name"], { path: "/" });
        setCookie("password_expiry_dtm", response.data["password_expiry_dtm"], {
          path: "/",
        });
        setCookie("locked_out_flag", response.data["locked_out_flag"], {
          path: "/",
        });
        setCookie("email", response.data["email"], { path: "/" });
        setCookie("ca_user_active_flag", response.data["ca_user_active_flag"], {
          path: "/",
        });
        setCookie(
          "company_user_active_flag",
          response.data["company_user_active_flag"],
          { path: "/" },
        );
        setCookie(
          "user_company_association_flag",
          response.data["user_company_association_flag"],
          { path: "/" },
        );
        setCookie("sns_id", response.data["sns_id"], { path: "/" });
        setCookie("message", response.data["message"], { path: "/" });
        setCookie("status", response.data["status"], { path: "/" });
        setCookie(
          "session_timeout_minutes",
          response.data["session_timeout_minutes"],
          { path: "/" },
        );

        // Save profile picture to localStorage (too large for cookies)
        const profilePictureUrl = response.data["profile_picture_url"] || "";
        if (profilePictureUrl) {
          try {
            localStorage.setItem("user_avatar", profilePictureUrl);
          } catch (error) {
            console.error(
              "Login - failed to save user_avatar to localStorage:",
              error,
            );
          }
        } else {
          try {
            localStorage.setItem("user_avatar", "");
          } catch (error) {
            console.error(
              "Login - failed to save user_avatar to localStorage:",
              error,
            );
          }
        }

        document.cookie = `isAuthenticated=true; path=/`;

        // Clear any existing logout events after successful login
        // This ensures clean state for the new user session
        const keys = Object.keys(localStorage);
        keys.forEach((key) => {
          if (key.startsWith("ca-logout-")) {
            localStorage.removeItem(key);
          }
        });

        // Broadcast login event to close other tabs with the same user
        const caUserId = response.data["ca_user_id"];
        if (caUserId) {
          // Set a flag to indicate this tab is the one that just logged in
          sessionStorage.setItem("just-logged-in", "true");
          localStorage.setItem(`ca-login-${caUserId}`, Date.now().toString());
          console.log(
            "Login: Broadcasting login event to close other tabs for user:",
            caUserId,
          );
        }

        if (response.data["user_pay_type"] == "Free User") {
          window.location.href = "/free-user";
        } else {
          if (refererUrl) {
            window.location.href = refererUrl;
          } else {
            window.location.href = "/dashboard";
          }
        }
      })
      .catch((error: any) => {
        console.log(error);
        setIsSubmitting(false);

        const resolvedMessage = error.details?.error_message || error.message;

        if (error.details?.account_locked === true) {
          setCookie("locked_email", v_email, { path: "/" });
          setAccountLockedMessage(
            resolvedMessage ||
              "Your account is temporarily locked because of repeated incorrect login attempts. Select FORGOT PASSWORD to reset your password.",
          );
          setIsAccountLockedModalOpen(true);
        } else {
          setError(resolvedMessage);
        }
      });
  };

  return (
    <AuthLayout
      title="Login"
      welcomeTitle="Welcome Back :)"
      leftBackgroundImage="/login-bg.jpg"
      darkTheme
      logoImage="/auth-white-logo.png"
      tagline="QUALITY. PRODUCTIVITY. COMPOUNDED."
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <div>
          <label className="block text-sm font-semibold text-white/90 mb-1.5">
            Email *
          </label>
          <TextField
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            variant="outlined"
            placeholder="johnsmith@gmail.com"
            error={!!error && !email}
            helperText={error && !email ? "Email is required" : ""}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                fontSize: "1rem",
                backgroundColor: "#fff",
                color: "#1e293b",
                "& fieldset": { borderColor: "#cbd5e1" },
                "&:hover fieldset": { borderColor: "#94a3b8" },
                "&.Mui-focused fieldset": {
                  borderWidth: "2px",
                  borderColor: "#153C7F",
                },
              },
              "& .MuiFormHelperText-root": {
                color: "#fca5a5",
                marginTop: "4px",
              },
            }}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-white/90 mb-1.5">
            Password *
          </label>
          <TextField
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            required
            variant="outlined"
            error={!!error && !password}
            helperText={error && !password ? "Password is required" : ""}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: "#64748b" }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                fontSize: "1rem",
                backgroundColor: "#fff",
                color: "#1e293b",
                "& fieldset": { borderColor: "#cbd5e1", },
                "&:hover fieldset": { borderColor: "#94a3b8" },
                "&.Mui-focused fieldset": {
                  borderWidth: "2px",
                  borderColor: "#153C7F",
                },
              },
              "& .MuiFormHelperText-root": {
                color: "#fca5a5",
                marginTop: "4px",
              },
            }}
          />
        </div>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isSubmitting}
          sx={{
            py: { xs: 2, sm: 1.75 },
            mt: 0.5,
            borderRadius: "6px",
            backgroundColor: "#63A821",
            color: "#fff",
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            boxShadow: "0 2px 12px rgba(99,168,33,0.35)",
            "&:hover": {
              backgroundColor: "#4e8c1a",
              boxShadow: "0 4px 14px rgba(99,168,33,0.4)",
            },
            "&.Mui-disabled": {
              backgroundColor: "#63A821",
              color: "#fff",
              opacity: 0.9,
            },
          }}
        >
          {isSubmitting ? "Logging in..." : "Login Now"}
        </Button>

        <div className="mt-3 text-right">
          <Link
            href="/forgot-password"
            className="text-sm text-gray-400 italic hover:text-gray-300 hover:not-italic underline font-medium"
          >
            Forgot Password?
          </Link>
        </div>
      </form>

      <WarningModal
        open={isAccountLockedModalOpen}
        onClose={() => setIsAccountLockedModalOpen(false)}
        onPrimary={handleAccountLockedConfirm}
        type="warning"
        title="ACCOUNT LOCKED"
        message={accountLockedMessage}
        primaryButtonText="FORGOT PASSWORD"
        secondaryButtonText="CANCEL"
        onSecondary={() => setIsAccountLockedModalOpen(false)}
      />
    </AuthLayout>
  );
}
